import { Close, VerifiedUser } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import Title from 'lib/designSystem/Title';
import {
  BaseOperation,
  OPERATION_TYPES,
} from 'pages/BusinessProfile/Contracts/Portfolios/Components/OperationAssociation/operations';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

interface OperationsDialogProps {
  open: boolean;
  onClose: () => void;
  operationType: string;
  onSelectOperation: (operation: BaseOperation) => void;
  businessIdentifier: string | undefined;
}

const OperationsDialog: React.FC<OperationsDialogProps> = ({
  open,
  onClose,
  operationType,
  onSelectOperation,
  businessIdentifier,
}) => {
  const dispatch = useDispatch();
  const [operations, setOperations] = useState<BaseOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(BASE_PAGINATION);

  const config = OPERATION_TYPES[operationType];
  const tableHeaders = useMemo(
    () => (config?.tableHeaders as Header[]) || [],
    [config],
  );

  const fetchOperations = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    if (!config) return;

    try {
      setLoading(true);
      const response = await config.fetchData(
        dispatch,
        page,
        pageSize,
        businessIdentifier,
      );
      setOperations(response.data);
      setPagination(response.pagination);
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOperation = (id: number) => {
    const operation = operations.find((o) => o.id === id);
    if (operation) {
      onSelectOperation(operation);
      onClose();
    }
  };

  useEffect(() => {
    if (open && config) {
      void fetchOperations({ page: 1, pageSize: 10 });
    }
  }, [open, operationType]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Title
            title="Operaciones"
            icon={<VerifiedUser color="error" />}
            color="error"
          />
        </DialogTitle>
        <DialogContent>
          <p>Tipo de operación no soportado</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title
          title={`Operaciones - ${config.label}`}
          icon={<VerifiedUser color="success" />}
          color="success"
        />
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableWithPagination
          data={operations}
          setData={setOperations}
          dataHeaders={tableHeaders}
          handleClickRow={(id: number) => {
            handleSelectOperation(id);
          }}
          showActions
          pagination={pagination}
          setPagination={setPagination}
          searchData={fetchOperations}
          setLoading={setLoading}
          exportToFile="xlsx"
          exportComplete
          lastElement
        />
      </DialogContent>
    </Dialog>
  );
};

export default OperationsDialog;
